import React, {
    useContext, useCallback, useState, useEffect
} from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import {
    ModalNextProvider, Loader, FormattedDate
} from '@jutro/components';
import { useValidation } from 'gw-portals-validation-react';
import { TranslatorContext } from '@jutro/locale';
import { WizardPage, wizardProps, WizardPageTemplate } from 'gw-portals-wizard-react';
import { withRouter } from 'react-router-dom';
import { LobIconUtil } from 'gw-portals-util-js';
import { ImageComponent } from 'gw-components-platform-react';
import { useDependencies } from 'gw-portals-dependency-react';
import { useAuthentication } from 'gw-digital-auth-react';
// eslint-disable-next-line import/no-unresolved
import appConfig from 'app-config';
import moment from 'moment';
import { getNormalizedLOBName } from 'gw-portals-config-js';
import { ViewModelForm, withViewModelService } from 'gw-portals-viewmodel-react';

import Claim from '../../models/Claim';
import styles from './FNOLSelectPolicy.module.scss';
import metadata from './FNOLSelectPolicy.metadata.json5';
import messages from '../../FNOL.messages';

const lobPolicyNameMapping = {
    BOP: 'Business Owners Policy',
    LRP: "Lessor's Risk Policy"
};
function FNOLSelectPolicy(props) {
    const {
        wizardData: claimVM,
        updateWizardData,
        updateWizardSnapshot,
        history,
        basePath,
        steps,
        goNext,
        history: {
            location: { state }
        }
    } = props;
    const {
        redirectPath,
        claimNumber,
        claimStatus
    } = state || {};
    const { FNOLService } = useDependencies('FNOLService');
    const { ClaimService } = useDependencies('ClaimService');
    const translator = useContext(TranslatorContext);
    const [policyData, setPolicyData] = useState({});
    const [policyNumber, setPolicyNumber] = useState();
    const [accountNumber, setAccountNumber] = useState();
    const [isLoading, setIsLoading] = useState(true);
    const [isShowInfoMessage, setInfoMessage] = useState(false);
    const [policyType, setSelectedPolicyType] = useState('');
    const [offeringList, setOfferingList] = useState([]);
    const [claimType, setClaimType] = useState('');
    const { authHeader } = useAuthentication();
    const { isComponentValid, registerComponentValidation } = useValidation('FNOLSelectPolicy');

    const getFormattedDate = useCallback((items, index, property) => {
        if (typeof items[property.id] === 'undefined') {
            return '';
        }
        return <FormattedDate value={new Date(items[property.id])} format="short" />;
    }, []);

    const processResponseData = useCallback((accountDetails) => {
        const policyDetails = [];
        if (appConfig.persona === 'policyholder') {
            if (!_.isEmpty(accountDetails)) {
                accountDetails.forEach((policyInfo) => {
                    const policy = {
                        product: policyInfo.policyNumber,
                        policyNumber: policyInfo.policyNumber,
                        productType: policyInfo.policyType,
                        effectiveDate: policyInfo.effectiveDate,
                        expirationDate: policyInfo.expirationDate,
                        offeringCode: policyInfo.offeringCode,
                        offeringName: policyInfo.offeringName
                    };
                    policyDetails.push(policy);
                });
            }
        } else {
            const lobindex = 0;
            const allowedPolicy = appConfig.allowedPolicyTypes;
            accountDetails.policySummaries.forEach((policyInfo) => {
                const { isCancelled } = policyInfo;
                const businessLines = policyInfo.policyLines;
                const { productName } = policyInfo.product;
                const businessLine = businessLines[lobindex].lineOfBusinessCode;
                if (
                    productName !== 'Commercial Package'
                    && _.includes(allowedPolicy, businessLine)
                    && !isCancelled
                    && !moment().isBefore(moment(policyInfo.effective))
                ) {
                    const policy = {
                        product: policyInfo.policyNumber,
                        policyNumber: policyInfo.policyNumber,
                        productType: businessLine,
                        effectiveDate: policyInfo.effectiveDate,
                        expirationDate: policyInfo.expirationDate,
                        offeringCode: policyInfo.offeringCode,
                        offeringName: policyInfo.offeringName
                    };
                    policyDetails.push(policy);
                }
            });
        }
        return policyDetails;
    }, []);

    const getOfferingCode = useCallback((selectedPolicyType) => {
        if (!_.isEmpty(selectedPolicyType)) {
            FNOLService.getOfferingCode(selectedPolicyType, authHeader).then((options) => {
                if (!_.isEmpty(options)) {
                    setOfferingList(options);
                    setClaimType(_.get(claimVM.value, 'typeOfClaim_Ext', ''));
                    if (!_.isEmpty(options) && options.length === 1) {
                        setClaimType(_.get(options[0], 'code', ''));
                    }
                }
            });
        }
    }, [FNOLService, authHeader, claimVM]);

    const checkPageSkipped = useCallback(() => {
        const stepsWithoutSelectPolicy = steps.slice(1, steps.length);
        return basePath !== '/fnol-select-policy'
        && !stepsWithoutSelectPolicy.some((step) => step.visited);
    }, [basePath, steps]);

    const getSearchPolicies = useCallback(
        () => {
            if (!checkPageSkipped()) {
                if (appConfig.persona === 'policyholder') {
                    const policySearchCriteria = {
                        lossDate: _.get(claimVM.value, 'lossDate')
                    };
                    FNOLService
                        .searchPolicies(policySearchCriteria, authHeader).then((response) => {
                            setPolicyData(processResponseData(response));
                            setIsLoading(false);
                            const availablePolicy = policyNumber
                || (!_.isEmpty(claimVM.value) && _.get(claimVM.value, 'policy.policyNumber'));
                            if (!_.isEmpty(availablePolicy)) {
                                if (!_.isEmpty(response)) {
                                    const selectedPolicyType = response.find((policy) => {
                                        return policy.policyNumber === availablePolicy;
                                    }).policyType;
                                    setSelectedPolicyType(selectedPolicyType);
                                    getOfferingCode(selectedPolicyType);
                                }
                            }
                        });
                } else {
                    setIsLoading(false);
                }
                _.set(claimVM.value, 'selectedPolicy', '');
                updateWizardData(claimVM);
            }
        },
        [FNOLService, authHeader, checkPageSkipped, claimVM, getOfferingCode,
            policyNumber, processResponseData, updateWizardData]
    );

    const redirectDraftClaimFlow = useCallback(() => {
        setIsLoading(true);
        if (_.isFunction(ClaimService.addRecentlyViewedClaim)) {
            ClaimService.addRecentlyViewedClaim(claimNumber, authHeader);
        }
        return FNOLService.getClaim(claimNumber, authHeader).then((getClaimResponse) => {
            setPolicyNumber(getClaimResponse.policy.policyNumber);
            claimVM.value = getClaimResponse;
            const lobCode = _.get(claimVM.value, 'policy.offeringCode_Ext') || _.get(claimVM.value, 'policy.policyType');
            const normalizedLobCode = getNormalizedLOBName(lobCode);
            return history.push({
                pathname: `/fnol-${normalizedLobCode}`,
                state: {
                    claimData: getClaimResponse,
                    redirectPath: redirectPath,
                    claimStatus: claimStatus
                }
            });
        });
    }, [
        ClaimService,
        claimNumber,
        authHeader,
        FNOLService,
        claimVM.value,
        history,
        redirectPath,
        claimStatus
    ]);

    useEffect(() => {
        if (!_.isEmpty(claimNumber)) {
            redirectDraftClaimFlow();
        }
        if (checkPageSkipped()) {
            goNext();
        }
        // This will call only once.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const policyNumberVar = _.isNil(policyNumber)
            ? _.get(claimVM.value, 'policy.policyNumber')
            : policyNumber;
        setPolicyNumber(policyNumberVar);
        if (
            (claimStatus
                !== translator({ id: 'typekey.ClaimState.draft', defaultMessage: 'draft' })
            )
            || appConfig.persona === 'policyholder'
        ) {
            getSearchPolicies();
        } else {
            setIsLoading(false);
        }
        // This will call only once.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const changePolicy = useCallback(() => {
        const previousClaimVM = _.clone(claimVM.value);
        claimVM.value = new Claim();
        _.set(claimVM.value, 'lossDate', _.get(previousClaimVM, 'lossDate'));
        _.set(claimVM.value, 'typeOfClaim_Ext', claimType);
        updateWizardData(claimVM);
    }, [claimType, claimVM, updateWizardData]);

    const callSearchPolicyService = useCallback(() => {
        const policySearchCriteria = {
            policyNumber: policyNumber,
            lossDate: _.get(claimVM.value, 'lossDate'),
            ClaimType_Ext: _.get(claimVM.value, 'typeOfClaim_Ext'),
            ReportedDate_Ext: _.get(claimVM.value, 'ReportedDate_Ext')
        };
        return FNOLService.searchPolicies(policySearchCriteria, authHeader).then(
            (policyResponse) => {
                if (_.isEmpty(policyResponse) && basePath === '/fnol-select-policy') {
                    setInfoMessage(true);
                    return claimVM;
                }
                if (_.isEmpty(policyResponse)) {
                    setInfoMessage(true);
                    return false;
                }
                const policyResponseExt = policyResponse[0];
                if (_.get(policyResponseExt, 'policyType') === 'WC7WorkersComp') {
                    _.set(claimVM.value, 'policy', policyResponse[0]);
                } else {
                    _.set(policyResponseExt, 'offeringCode_Ext', _.get(policyResponseExt, 'offeringCode', ''));
                    _.set(policyResponseExt, 'offeringName_Ext', _.get(policyResponseExt, 'offeringName', ''));
                    _.set(claimVM.value, 'policy', policyResponseExt);
                }
                _.set(claimVM.value, 'policy.accountNumber', accountNumber);
                updateWizardData(claimVM);
                updateWizardSnapshot(claimVM);
                const lobCode = _.get(claimVM.value, 'policy.offeringCode') || _.get(claimVM.value, 'policy.policyType');
                const normalizedLobCode = getNormalizedLOBName(lobCode);
                return history.push({
                    pathname: `/fnol-${normalizedLobCode}`,
                    state: {
                        claimData: JSON.parse(JSON.stringify(claimVM.value)),
                        redirectPath: redirectPath
                    }
                });
            }
        );
    }, [policyNumber, claimVM, FNOLService, authHeader, basePath, accountNumber,
        updateWizardData, updateWizardSnapshot, history, redirectPath]);

    const onNext = useCallback(() => {
        const prevPolicyNo = _.get(claimVM.value, 'policy.policyNumber');
        if (!_.isEmpty(prevPolicyNo) && !_.isEmpty(policyNumber) && prevPolicyNo !== policyNumber) {
            return ModalNextProvider.showConfirm({
                title: messages.policyChangeModalTitle,
                message: messages.policyChangeModalMessage,
                status: 'warning',
                icon: 'mi-error-outline',
                confirmButtonText: messages.changePolicyButtonlabel,
                cancelButtonText: messages.fnolMessagePopupCancel
            }).then((results) => {
                if (results === 'cancel') {
                    return _.noop();
                }
                changePolicy();
                return callSearchPolicyService();
            }, _.noop);
        }
        return callSearchPolicyService();
    }, [callSearchPolicyService, changePolicy, claimVM.value, policyNumber]);

    const onPolicySelect = useCallback(
        (selectedValue) => {
            if (_.get(claimVM.value, 'policy.accountNumber')) {
                setAccountNumber(_.get(claimVM.value, 'policy.accountNumber'));
            }
            _.set(claimVM.value, 'selectedPolicy', selectedValue);
            _.set(claimVM.value, 'typeOfClaim_Ext', '');
            _.set(claimVM.value, 'coverageType_Ext', '');
            setPolicyNumber(selectedValue);

            const selectedPolicyType = policyData.find((policy) => {
                return policy.policyNumber === selectedValue;
            }).productType;
            setSelectedPolicyType(selectedPolicyType);
            getOfferingCode(selectedPolicyType);
        },
        [claimVM, getOfferingCode, policyData]
    );

    const getCell = useCallback(
        (item, index, property) => {
            return <span>{item[property.id]}</span>;
        },
        []
    );

    const generateDataForRadioButton = useCallback((row) => {
        return {
            code: row.policyNumber,
            value: ''
        };
    }, []);

    const getProductImage = useCallback((item) => {
        const offeringCode = _.get(item, 'offeringCode', '');
        const offeringName = _.get(item, 'offeringName', '');
        const title = lobPolicyNameMapping[offeringName]
            ? lobPolicyNameMapping[offeringName] : offeringName;
        return (
            <ImageComponent
                src={`/branding/${LobIconUtil.getImageIcon(item.productType, offeringCode)}.svg`}
                alt={item.productType}
                title={title}
            />
        );
    }, []);

    const getPolicyType = useCallback((item, index, property) => {
        const mappedOfferingCode = _.get(appConfig, `TDICOfferingCodeMapCCPC.${item[property.id]}`, '');
        return _.get(appConfig, `TDICAbbreviations.${mappedOfferingCode}`, '');
    }, []);

    const dateIsInFuture = useCallback((date) => {
        if (!date) {
            return false;
        }

        const currentTime = new Date();
        const selectedDate = new Date(date);
        return selectedDate > currentTime;
    }, []);

    const isPageValid = useCallback(() => {
        if (_.isEmpty(policyNumber) && _.isEmpty(_.get(claimVM.value, 'policy.policyNumber'))) {
            return false;
        }
        if (!_.get(claimVM.value, 'lossDate') || dateIsInFuture(_.get(claimVM.value, 'lossDate'))) {
            return false;
        }
        if (_.isEmpty(policyType) || (_.isEmpty(claimType))) {
            return false;
        }
        return true;
    }, [claimType, claimVM, dateIsInFuture, policyNumber, policyType]);

    useEffect(() => {
        registerComponentValidation(isPageValid);
    }, [isPageValid, registerComponentValidation]);

    const getPolicyNumberHeading = useCallback(() => {
        const previousPolicyNumber = _.get(claimVM.value, 'policy.policyNumber');
        const policyNumberToDisplay = policyNumber || previousPolicyNumber || '';
        return translator(messages.lossInformation, { policyNumber: policyNumberToDisplay });
    }, [claimVM.value, translator, policyNumber]);

    const dateOnBlur = useCallback(() => {
        getSearchPolicies(_.get(claimVM.value, 'policy.accountNumber'));
    }, [claimVM, getSearchPolicies]);

    const onDateChange = useCallback((value) => {
        _.set(claimVM.value, 'lossDate', new Date(value));
        _.set(claimVM.value, 'ReportedDate_Ext', new Date());
        updateWizardData(claimVM);
    }, [claimVM, updateWizardData]);

    const onClaimTypeChange = useCallback((value) => {
        setClaimType(value);
        _.set(claimVM.value, 'typeOfClaim_Ext', value);
        _.set(claimVM.value, 'coverageType_Ext', '');
        updateWizardData(claimVM);
    }, [claimVM, updateWizardData]);

    const onCoverageSelect = useCallback(() => {
        if (claimVM.value.typeOfClaim_Ext === 'cyberliability'
            && _.isEmpty(claimVM.value.coverageType_Ext)) {
            return false;
        }
        return true;
    }, [claimVM]);

    const overrides = {
        policyTable: {
            data: policyData
        },
        policyTableHeadingId: {
            visible: policyData.length > 0
        },
        policyLossDateHeadingId: {
            content: getPolicyNumberHeading()
        },
        policySelectRadio: {
            option: generateDataForRadioButton,
            value:
                policyNumber
                || (!_.isEmpty(claimVM.value) && _.get(claimVM.value, 'policy.policyNumber')),
            onValueChange: onPolicySelect
        },
        noPolicyInfoLableId: {
            visible: isShowInfoMessage
        },
        lossDateId: {
            value: _.get(claimVM.value, 'lossDate'),
            minDate: new Date(_.get(appConfig, 'tdic.dateRange.minDate'), 0),
            maxDate: new Date(_.get(appConfig, 'tdic.dateRange.maxDate'), 0),
            onValueChange: onDateChange,
            onBlur: dateOnBlur
        },
        reportedDate: {
            value: new Date()
        },
        typeOfClaim: {
            availableValues: offeringList,
            value: claimType,
            onValueChange: onClaimTypeChange
        },
        policyTypeSelect: {
            value: policyType
        },
        coverageType: {
            visible: claimVM.value.typeOfClaim_Ext === 'cyberliability'
        },
        firstPartyCoverageTypeText: {
            visible: claimVM.value.typeOfClaim_Ext === 'cyberliability'
                && claimVM.value.coverageType_Ext === 'firstparty'
        },
        secondPartyCoverageTypeText: {
            visible: claimVM.value.typeOfClaim_Ext === 'cyberliability'
                && claimVM.value.coverageType_Ext === 'thirdparty'
        }
    };
    const resolvers = {
        resolveCallbackMap: {
            getCell: getCell,
            getFormattedDate: getFormattedDate,
            getProductImage: getProductImage,
            getPolicyType: getPolicyType
        },
        resolveClassNameMap: styles
    };

    if (isLoading) {
        return <Loader loaded={!isLoading} />;
    }

    const isValid = isComponentValid && onCoverageSelect();

    return (
        <WizardPage
            onNext={onNext}
            showPrevious={false}
            disableNext={!isValid}
            template={WizardPageTemplate}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={claimVM}
                overrideProps={overrides}
                onModelChange={updateWizardData}
                callbackMap={resolvers.resolveCallbackMap}
                classNameMap={resolvers.resolveClassNameMap}
            />
        </WizardPage>
    );
}

FNOLSelectPolicy.propTypes = wizardProps;
FNOLSelectPolicy.propTypes = {
    history: PropTypes.shape({
        push: PropTypes.func
    }).isRequired
};

export default withRouter(withViewModelService(FNOLSelectPolicy));
